import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl } from "~/modules/cbm";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import {
    checkChannelSid,
    checkInteractionSid,
    checkReservationExists,
    getReservationsByTask
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";





























export class LeaveConversation implements Action<Promise<void>> {
    


    readonly #taskSid: string;

    constructor(taskSid: string) {
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        logger.debug(`LeaveConversation invoked with taskSid: ${this.#taskSid}`);

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "LeaveConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.LeaveConversationCompleted,
            extra: { taskSid: this.#taskSid },
            logger
        });

        const task = reservation.task;

        const interactionSid = task.attributes.flexInteractionSid as string | undefined;
        const channelSid = task.attributes.flexInteractionChannelSid as string | undefined;

        checkInteractionSid(interactionSid, this.#taskSid, {
            actionName: "LeaveConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.LeaveConversationCompleted,
            extra: { taskSid: this.#taskSid, interactionSid: interactionSid! },
            logger
        });

        checkChannelSid(channelSid, this.#taskSid, {
            actionName: "LeaveConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.LeaveConversationCompleted,
            extra: { taskSid: this.#taskSid, interactionSid: interactionSid! },
            logger
        });

        try {
            await cbm.leaveChannel(interactionSid!, channelSid!, "inactive", "closed");

            sendWorkerTrackingEvent(EVENTS.LeaveConversationCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                interactionSid: interactionSid!,
                channelSid: channelSid!
            });

            return Promise.resolve();
        } catch (error) {
            const errorMsg = `LeaveConversation: Failed to leave conversation for task ${
                this.#taskSid
            }, interactionSid ${interactionSid!}, channelSid ${channelSid!}`;
            logger.error(errorMsg, error);

            sendWorkerTrackingEvent(EVENTS.LeaveConversationCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                interactionSid: interactionSid!,
                channelSid: channelSid!
            });

            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToLeaveConversation,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
